/**
 * Theme → Custom theme: the prototype's editor (design/reboot/themes.js `editor`, `sample`, `init`)
 * ported one-to-one — intro with Export, the live sample with contrast ratios, name and base, fonts and
 * installed families, type sizes and line heights, the 18 colours (swatch + hex), spacing & shape, soft
 * shadows, desktop pane widths, Import / Reset. Values are data: every one passes `normalizeTheme`
 * before it reaches the document; no CSS text is executed.
 *
 * Save stores `settings.customTheme` and makes Custom the active theme (as the prototype's `collect`
 * does). On a plan without the custom theme the engine refuses (`not_entitled`) and the upsell opens.
 */

import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { DEFAULT_CUSTOM_THEME, toDigesterError, type ThemeColorKey, type ThemeDefinition, type ThemeFont } from '@digester/core';
import { showToast } from '../../app/Toast.js';
import { useEngine } from '../../engine-client/EngineProvider.js';
import { qk } from '../../engine-client/keys.js';
import { Icon } from '../../icons/Icon.js';
import {
  THEME_COLOR_LABELS, THEME_FONTS, THEME_NUMBER_SPECS, contrastRatio, exportThemeFile, fontFamily, normalizeTheme, parseThemeFile, type ThemeNumberKey,
} from '../../theme/normalize.js';
import { Field, Section, Select, Toggle } from '../../ui/form.js';
import { ProMark, readTextFile, saveJsonFile } from '../../account/shared.js';
import { EditorFrame, useDraft } from '../shell/index.js';
import type { ThemeEditorProps } from './editors.js';
import { useAppearancePreview } from './preview.js';

const HEX = /^#[\da-f]{6}$/i;
const MAX_THEME_FILE = 100_000;

const TYPE_KEYS: readonly ThemeNumberKey[] = ['uiSize', 'readingSize', 'headingSize', 'uiLineHeight', 'readingLineHeight', 'letterSpacing'];
const SHAPE_KEYS: readonly ThemeNumberKey[] = ['rowPadding', 'readerPadding', 'sectionGap', 'controlHeight', 'toolbarHeight', 'radius', 'borderWidth', 'iconSize'];
const PANE_KEYS: readonly ThemeNumberKey[] = ['navWidth', 'listWidth', 'detailsWidth', 'articleWidth'];

function spec(key: ThemeNumberKey) {
  const found = THEME_NUMBER_SPECS.find((s) => s[0] === key);
  if (!found) throw new Error(`unknown theme number ${key}`);
  return found;
}

/** A number input that keeps what is typed (validated by min/max/step on Save) and commits finite values. */
function NumberField({ name, value, onChange }: { name: ThemeNumberKey; value: number; onChange(value: number): void }) {
  const [, label, , min, max, step, unit] = spec(name);
  const [text, setText] = useState(String(value));
  useEffect(() => {
    if (Number(text) !== value) setText(String(value));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- follow outside changes (import, reset) only
  }, [value]);
  return (
    <label className="field">
      <span>
        {label} <small>{unit}</small>
      </span>
      <input
        name={`theme-${name}`}
        type="number"
        min={min}
        max={max}
        step={step}
        required
        value={text}
        onChange={(e) => {
          setText(e.target.value);
          const n = Number(e.target.value);
          if (e.target.value !== '' && Number.isFinite(n)) onChange(n);
        }}
      />
    </label>
  );
}

/** Colour swatch + hex field (the prototype's `.theme-color`). */
function ColorField({ name, label, value, onChange }: { name: ThemeColorKey; label: string; value: string; onChange(value: string): void }) {
  const [text, setText] = useState(value);
  useEffect(() => {
    if (text.toLowerCase() !== value.toLowerCase()) setText(value);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- follow outside changes only
  }, [value]);
  return (
    <label className="theme-color">
      <span>{label}</span>
      <span>
        <input
          type="color"
          aria-label={`Choose ${label.toLowerCase()}`}
          data-color-swatch={name}
          value={HEX.test(text) ? text.toLowerCase() : value}
          onChange={(e) => {
            setText(e.target.value);
            onChange(e.target.value);
          }}
        />
        <input
          name={`theme-color-${name}`}
          aria-label={`${label} hex`}
          value={text}
          pattern="#[a-fA-F0-9]{6}"
          maxLength={7}
          required
          onChange={(e) => {
            setText(e.target.value);
            if (HEX.test(e.target.value)) onChange(e.target.value.toLowerCase());
          }}
        />
      </span>
    </label>
  );
}

/** The live sample and the two contrast ratios (the prototype's `sample(t)`). */
export function ThemeSample({ theme }: { theme: ThemeDefinition }) {
  const t = normalizeTheme(theme);
  const style = {
    '--sample-bg': t.colors.reader,
    '--sample-text': t.colors.body,
    '--sample-heading': t.colors.text,
    '--sample-accent': t.colors.accent,
    '--sample-border': t.colors.border,
    '--sample-radius': `${t.radius}px`,
    '--sample-font': fontFamily(t.readingFont, t.readingFamily),
    '--sample-size': `${Math.min(t.readingSize, 22)}px`,
    '--sample-leading': String(t.readingLineHeight),
  } as CSSProperties;
  const article = contrastRatio(t.colors.body, t.colors.reader);
  const labels = contrastRatio(t.colors.muted, t.colors.surface);
  return (
    <div id="theme-live-preview">
      <div className="theme-sample" style={style}>
        <span>
          Signal Review <span>4 min read</span>
        </span>
        <h3>A reading space that feels like yours</h3>
        <p>Your fonts, colours, and spacing. Preview your choices here before applying them to the workspace.</p>
        <button type="button" tabIndex={-1}>
          Priority
        </button>
      </div>
      <div className="theme-contrast">
        <span>
          Article contrast <strong data-contrast="article">{article.toFixed(1)}:1</strong>
        </span>
        <span>
          Labels <strong data-contrast="labels">{labels.toFixed(1)}:1</strong>
        </span>
        {article < 4.5 || labels < 4.5 ? <span className="contrast-note">Consider more contrast for small text.</span> : null}
      </div>
    </div>
  );
}

export function CustomThemeEditor({ settings, entitlement }: ThemeEditorProps) {
  const engine = useEngine();
  const queryClient = useQueryClient();
  const fileInput = useRef<HTMLInputElement>(null);
  const [importResult, setImportResult] = useState('');
  const draft = useDraft<ThemeDefinition>({
    initial: normalizeTheme(settings.customTheme),
    onSave: async (value) => {
      const theme = normalizeTheme(value);
      // Activating Custom is what the plan decides on; the engine refuses it on Free (→ upsell).
      if (settings.appearance.theme !== 'custom') queryClient.setQueryData(qk.settings(), await engine.settings.set('appearance', { ...settings.appearance, theme: 'custom' }));
      queryClient.setQueryData(qk.settings(), await engine.settings.set('customTheme', theme));
    },
  });
  const t = draft.value;
  const saved = { appearance: settings.appearance, reading: settings.reading, customTheme: settings.customTheme, limits: entitlement.limits };
  useAppearancePreview(saved, { ...saved, appearance: { ...settings.appearance, theme: 'custom' }, customTheme: normalizeTheme(t) }, draft.dirty);

  const set = <K extends keyof ThemeDefinition>(key: K, value: ThemeDefinition[K]): void => draft.set((d) => ({ ...d, [key]: value }));
  const setColor = (key: ThemeColorKey, value: string): void => draft.set((d) => ({ ...d, colors: { ...d.colors, [key]: value } }));

  const fontControls = (which: 'ui' | 'reading', label: string) => (
    <div>
      <Select<ThemeFont> label={label} name={`theme-${which}Font`} value={t[`${which}Font`]} options={THEME_FONTS} onChange={(v) => set(`${which}Font`, v)} />
      <Field
        label="Installed font family"
        name={`theme-${which}Family`}
        value={t[`${which}Family`]}
        placeholder="For example: IBM Plex Sans"
        onChange={(v) => set(`${which}Family`, v)}
      />
    </div>
  );

  const importFile = async (file: File): Promise<void> => {
    try {
      const theme = parseThemeFile(await readTextFile(file, MAX_THEME_FILE), MAX_THEME_FILE);
      draft.set(theme);
      setImportResult(`Imported “${theme.name}”. Save to apply.`);
      showToast('Theme imported · Save to apply');
    } catch (error) {
      const message = error instanceof Error && error.message === 'The file is too large' ? 'Theme file is too large' : toDigesterError(error).message;
      setImportResult(message);
      showToast(message);
    }
  };

  return (
    <EditorFrame title="Custom theme" icon="palette">
      <div className="custom-theme-editor">
        <div className="theme-editor-intro">
          <div>
            <span className="theme-status">
              {settings.appearance.theme === 'custom' && entitlement.limits.customTheme ? 'Active theme' : 'Customisable'}
              {entitlement.limits.customTheme ? null : <ProMark title="The custom theme is included in Pro" />}
            </span>
            <p>Make the workspace your own. Save to apply.</p>
          </div>
          <button
            type="button"
            className="secondary-button"
            onClick={() => {
              saveJsonFile('digester-theme.json', exportThemeFile(t));
              showToast('Theme exported');
            }}
          >
            <Icon name="download" /> Export theme
          </button>
        </div>
        <ThemeSample theme={t} />
        <Section title="Theme">
          <Field label="Name" name="theme-name" value={t.name} required maxLength={60} onChange={(v) => set('name', v)} />
          <Select<'dark' | 'light'>
            label="Base"
            name="theme-base"
            value={t.base}
            options={[
              ['dark', 'Dark'],
              ['light', 'Light'],
            ]}
            onChange={(v) => set('base', v)}
          />
        </Section>
        <Section title="Typography">
          <div className="theme-font-columns">
            {fontControls('ui', 'Interface font')}
            {fontControls('reading', 'Article font')}
          </div>
          <div className="theme-number-grid">
            {TYPE_KEYS.map((key) => (
              <NumberField key={key} name={key} value={t[key]} onChange={(v) => set(key, v)} />
            ))}
          </div>
        </Section>
        <Section title="Colours">
          <div className="theme-color-grid">
            {THEME_COLOR_LABELS.map(([key, label]) => (
              <ColorField key={key} name={key} label={label} value={t.colors[key]} onChange={(v) => setColor(key, v)} />
            ))}
          </div>
        </Section>
        <Section title="Spacing & shape">
          <div className="theme-number-grid">
            {SHAPE_KEYS.map((key) => (
              <NumberField key={key} name={key} value={t[key]} onChange={(v) => set(key, v)} />
            ))}
          </div>
          <Toggle label="Soft shadows" name="theme-shadows" checked={t.shadows} onChange={(v) => set('shadows', v)} />
        </Section>
        <Section title="Desktop panes">
          <div className="theme-number-grid">
            {PANE_KEYS.map((key) => (
              <NumberField key={key} name={key} value={t[key]} onChange={(v) => set(key, v)} />
            ))}
          </div>
          <p className="field-help">Pane widths apply on desktop. Narrow screens keep the mobile navigation.</p>
        </Section>
        <div className="theme-bottom-actions">
          <button type="button" className="secondary-button" onClick={() => fileInput.current?.click()}>
            <Icon name="download" /> Import theme
          </button>
          <input
            ref={fileInput}
            type="file"
            id="theme-file"
            accept="application/json,.json"
            hidden
            onChange={(e) => {
              const file = e.target.files?.[0];
              e.target.value = '';
              if (file) void importFile(file);
            }}
          />
          <button
            type="button"
            className="secondary-button"
            onClick={() => {
              draft.set(normalizeTheme(DEFAULT_CUSTOM_THEME));
              setImportResult('');
              showToast('Custom values reset · Save to apply');
            }}
          >
            Reset custom values
          </button>
        </div>
        <p className="field-help" id="theme-import-result" role="status">
          {importResult}
        </p>
      </div>
    </EditorFrame>
  );
}
